import React, { lazy, Suspense, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import { pageview } from "./utils/facebookPixel";

// Lazy-loaded pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Register = lazy(() => import("./pages/Register"));
const Organiser = lazy(() => import("./pages/Organiser"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Contacts = lazy(() => import("./pages/Contacts"));

// Component to track page views
function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    pageview();
  }, [location]);

  return null;
}

export default function App() {
  const logo = "/images/logo.png";
  return (
    <Suspense
      fallback={
        <div className="flex justify-center items-center h-screen">
          <div className="w-12 h-12 border-4 border-t-transparent border-[#236b8e] rounded-full animate-spin" />
        </div>
      }
    >
      <Helmet>
        <title>Stovykla „Spartiečiai" - Vaikų stovykla Lietuvoje</title>
        <meta
          name="description"
          content='Stovykla „Spartiečiai" - vaikų stovykla berniukams ir mergaitėms nuo 9 iki 15 metų. Aktyvus poilsis, žygiai, sportas ir ugdymas gamtos apsuptyje.'
        />
        <meta
          name="keywords"
          content="vaikų stovykla, stovykla Lietuvoje, vaikų poilsis, stovykla Spartiečiai, vaikų stovykla Kaunas, kauno rajono visuomenės sveikatos biuras"
        />
        <meta
          property="og:title"
          content='Stovykla „Spartiečiai" - Vaikų stovykla Lietuvoje'
        />
        <meta
          property="og:description"
          content='Stovykla „Spartiečiai" - vaikų stovykla berniukams ir mergaitėms nuo 9 iki 15 metų. Aktyvus poilsis, žygiai, sportas ir ugdymas gamtos apsuptyje.'
        />
        <meta property="og:image" content={logo} />
        <meta property="og:url" content="https://stovyklaspartieciai.lt" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://stovyklaspartieciai.lt" />
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "name": "Stovykla Spartiečiai",
          "url": "https://stovyklaspartieciai.lt",
          "logo": "https://stovyklaspartieciai.lt/images/kauno_rsvb_logo.jpg",
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+37065688719",
            "email": "ra.dambrauskas@gmail.com",
            "contactType": "Customer Service"
          }
        },
        {
          "@type": "Person",
          "name": "Ramūnas Dambrauskas",
          "jobTitle": "Stovyklos vadovas",
          "worksFor": {
            "@type": "Organization",
            "name": "Stovykla Spartiečiai"
          },
          "email": "ra.dambrauskas@gmail.com",
          "telephone": "+37065688719"
        },
        {
          "@type": "Person",
          "name": "Diana Čermašencevaitė",
          "jobTitle": "Vadovė, trenerė",
          "worksFor": {
            "@type": "Organization",
            "name": "Stovykla Spartiečiai"
          }
        },
        {
          "@type": "Event",
          "name": "Vasaros stovykla vaikams „Spartiečiai“ – birželio pamaina",
          "startDate": "2025-06-29",
          "endDate": "2025-07-04",
          "location": {
            "@type": "Place",
            "name": "Pušyno poilsiavietė",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Jadagonių k.",
              "addressRegion": "Kauno r.",
              "postalCode": "53404",
              "addressCountry": "LT"
            }
          },
          "offers": {
            "@type": "Offer",
            "price": "320",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://stovyklaspartieciai.lt/registracija"
          }
        },
        {
          "@type": "Event",
          "name": "Vasaros stovykla vaikams „Spartiečiai“ – liepos pamaina",
          "startDate": "2025-07-13",
          "endDate": "2025-07-17",
          "location": {
            "@type": "Place",
            "name": "Sodyba pas Rapolą",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Krekenava",
              "addressRegion": "Panevėžio raj.",
              "postalCode": "38302",
              "addressCountry": "LT"
            }
          },
          "offers": {
            "@type": "Offer",
            "price": "320",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://stovyklaspartieciai.lt/registracija"
          }
        },
        {
          "@type": "Event",
          "name": "Vasaros stovykla vaikams „Spartiečiai“ – rugpjūčio pamaina",
          "startDate": "2025-08-03",
          "endDate": "2025-08-08",
          "location": {
            "@type": "Place",
            "name": "Pušyno poilsiavietė",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Jadagonių k.",
              "addressRegion": "Kauno r.",
              "postalCode": "53404",
              "addressCountry": "LT"
            }
          },
          "offers": {
            "@type": "Offer",
            "price": "320",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://stovyklaspartieciai.lt/registracija"
          }
        }
      ]
    }
  `}
        </script>
      </Helmet>
      <div className="flex flex-col justify-between min-h-screen font-sans antialiased">
        <Router>
          <PageViewTracker />
          {/* Header */}
          <header className="w-full py-6 px-4 flex flex-col items-center shadow-md font-metropolis bg-[#0f2c49]">
            <img src={logo} alt="Spartan Logo" className="w-16 h-16 mb-2" />
            <h1 className="text-2xl sm:text-3xl font-bold text-yellow-400">
              Stovykla „Spartiečiai“
            </h1>
            <p className="text-sm text-white italic">
              kur auga drąsa ir draugystė
            </p>
            <nav className="flex flex-wrap justify-center gap-4 text-sm mt-4">
              <Link
                to="/"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Pradžia
              </Link>
              <Link
                to="/apie"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Apie
              </Link>
              <Link
                to="/vadovai"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Vadovai
              </Link>
              <Link
                to="/registracija"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Registracija
              </Link>
              <Link
                to="/galerija"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Galerija
              </Link>
              <Link
                to="/kontaktai"
                className="text-white hover:text-yellow-300 font-medium transition"
              >
                Kontaktai
              </Link>
            </nav>
          </header>

          {/* Main Content with Suspense fallback */}
          <main className="flex-1 w-full font-metropolis bg-white">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/apie" element={<About />} />
              <Route path="/registracija" element={<Register />} />
              <Route path="/vadovai" element={<Organiser />} />
              <Route path="/galerija" element={<Gallery />} />
              <Route path="/kontaktai" element={<Contacts />} />
            </Routes>
          </main>

          {/* Footer */}
          <footer className="bg-[#0f2c49] w-full text-center py-6 text-sm text-white font-metropolis border-t border-[#236b8e]">
            © {new Date().getFullYear()} Stovykla „Spartiečiai“ | Visos teisės
            saugomos
          </footer>
        </Router>
      </div>
    </Suspense>
  );
}
